import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	IconButton,
	InputAdornment,
	MenuItem,
	Pagination,
	Paper,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import routes from 'constants/routes';

import { pageSizeOptions } from 'constants/constants';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { defaultAlertState } from 'features/common/constants';
import { convertFiltersToRecord, createSortHandler, getSortableFields } from 'features/common/helpers';
import { useDebounceState } from 'features/common/hooks/useDebounceState';
import { FilterModel, IHeadCell, ISort, ShowAlertState } from 'features/common/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { findCountryWithAgencyConfigForUser } from 'features/auth/helpers';
import { openSaleOrderServiceUrl } from 'pages/SalesOrder/SalesOrderScreen/helpers';

import { getServicesList } from '../../services';
import { IServiceListItem } from '../../types';
import { serviceTypeToEmoji } from '../../constants';
import {
	areServicesListSearchesEqual,
	buildServicesListSearch,
	normalizeServicesListSearch,
	parseServicesListState,
	readInitialServicesListState,
	saveServicesListSearch,
	saveShowOptionalColumns,
	toServicesListSearchParam,
} from '../../helpers/servicesListState';
import { useServicesBulkSelection } from '../../hooks/useServicesBulkSelection';
import { BulkEditRowResult } from '../../helpers/servicesBulkEdit';
import {
	applyServicesQuickDateFilters,
	EMPTY_QUICK_DATE_STATE,
	ServicesQuickDateState,
	stripQuickDateConflictsFromFilters,
} from '../../helpers/servicesQuickDateFilters';
import { BulkSelectionBar } from './BulkSelectionBar';
import { ServicesFilters } from './ServicesFilters';
import { ServicesQuickDateBar } from './ServicesQuickDateBar';
import { ServiceQuickEditDialog } from './ServiceQuickEditDialog';
import { ServicesBulkEditDialog } from './ServicesBulkEditDialog';
import { DEFAULT_VISIBLE_COLUMNS, serviceListHeaderCells, transformServiceValue } from './constants';
import { stickyWidthsServices, useStyles } from './styles';

function renderEllipsisText(text: string | null | undefined, maxWidth = 220): JSX.Element | string {
	if (!text) return '-';

	return (
		<Box
			title={text}
			sx={{
				maxWidth,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			<Typography variant="body2" noWrap>
				{text}
			</Typography>
		</Box>
	);
}

function renderFullIdCell(value: string | null | undefined): JSX.Element | string {
	if (!value) return '-';

	return (
		<Box
			title="Doble clic para abrir la SaleOrder. Ctrl/⌘ + clic para abrir en nueva pestaña"
			sx={{
				whiteSpace: 'normal',
				wordBreak: 'break-all',
				lineHeight: 1.25,
				display: '-webkit-box',
				WebkitLineClamp: 2,
				WebkitBoxOrient: 'vertical',
				overflow: 'hidden',
			}}
		>
			<Typography variant="body2" sx={{ fontWeight: 400, color: 'text.primary' }}>
				{value}
			</Typography>
		</Box>
	);
}

type CellRenderer = (row: IServiceListItem) => JSX.Element | string | number | null;

function renderServiceTypeCell(row: IServiceListItem) {
	const info = serviceTypeToEmoji[row.serviceType];
	if (!info) return row.serviceType;

	return (
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
			<Box
				sx={{
					width: 18,
					height: 18,
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					flex: '0 0 auto',
				}}
			>
				<Typography component="span" variant="body2" sx={{ lineHeight: 1 }}>
					{info.emoji}
				</Typography>
			</Box>
			<Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
				{info.name}
			</Typography>
		</Box>
	);
}

function renderEnableCell(row: IServiceListItem) {
	return row.enable ? <CheckCircleIcon sx={{ color: 'success.main' }} /> : <CancelIcon sx={{ color: 'error.main' }} />;
}

function renderVoucherNotesCell(row: IServiceListItem): JSX.Element | string {
	return renderEllipsisText(row.voucherNotes, 240);
}

function renderInternalNotesCell(row: IServiceListItem): JSX.Element | string {
	return renderEllipsisText(row.internalNotes, 240);
}

function renderAgencyCell(row: IServiceListItem): JSX.Element {
	const countryToRender = findCountryWithAgencyConfigForUser(row.agencyId);

	if (!countryToRender) {
		return <Chip label={`Agencia ${row.agencyId}`} size="small" variant="outlined" />;
	}

	return (
		<Box
			display="inline-flex"
			alignItems="center"
			gap={0.75}
			sx={{
				px: 1,
				py: 0.25,
				borderRadius: 999,
				border: '1px solid',
				borderColor: 'divider',
				backgroundColor: 'background.paper',
				maxWidth: '100%',
			}}
		>
			<img
				src={countryToRender.agencyIcon}
				alt={countryToRender.countryName}
				style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover' }}
			/>
			<Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
				{countryToRender.countryName}
			</Typography>
		</Box>
	);
}

function renderCaseOrDealCell(row: IServiceListItem): JSX.Element | string {
	if (!row.saleOrderDealId) return '-';

	if (row.saleOrderIsBitrixDeal) {
		return <Typography variant="body2">{row.saleOrderDealId}</Typography>;
	}

	return (
		<Tooltip title="Ver detalle del caso en el CRM" arrow>
			<Box
				component="a"
				href={`https://crm.mevuelo.com/dashboard/cases/${row.saleOrderDealId}`}
				target="_blank"
				rel="noopener noreferrer"
				onClick={(event) => event.stopPropagation()}
				sx={{
					color: 'primary.main',
					textDecoration: 'none',
					fontWeight: 500,
					cursor: 'pointer',
					'&:hover': { textDecoration: 'underline' },
				}}
			>
				{row.saleOrderDealId}
			</Box>
		</Tooltip>
	);
}

const serviceCellRenderers: Record<string, CellRenderer> = {
	serviceType: renderServiceTypeCell,
	saleOrderDealId: renderCaseOrDealCell,
	enable: renderEnableCell,
	agencyId: renderAgencyCell,
	voucherNotes: renderVoucherNotesCell,
	internalNotes: renderInternalNotesCell,
};

const FILTER_FETCH_DEBOUNCE_MS = 80;

export const ServicesDataList = (): JSX.Element => {
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const initialListStateRef = useRef(readInitialServicesListState());
	const initialListState = initialListStateRef.current;
	const lastPushedSearchRef = useRef(
		buildServicesListSearch({
			search: initialListState.search,
			page: initialListState.page,
			size: initialListState.size,
			sortFields: initialListState.sortFields,
			filterApplied: initialListState.filterApplied,
			quickDateState: initialListState.quickDateState,
		}),
	);
	const lastDisplayedItemsRef = useRef<IServiceListItem[]>([]);
	const [isListHydrated, setIsListHydrated] = useState(true);

	const [loading, setLoading] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [currentPage, setCurrentPage] = useState(initialListState.page);
	const [currentSize, setCurrentSize] = useState(initialListState.size);
	const [pageTotal, setPageTotal] = useState(0);
	const [totalRecords, setTotalRecords] = useState(0);
	const [searchValue, setSearchValue] = useState(initialListState.search);
	const [sortFields, setSortFields] = useState<ISort[]>(initialListState.sortFields);
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>(initialListState.filterApplied);
	const [quickDateState, setQuickDateState] = useState<ServicesQuickDateState>(
		initialListState.quickDateState ?? EMPTY_QUICK_DATE_STATE,
	);
	const [showOptionalColumns, setShowOptionalColumns] = useState(initialListState.showOptionalColumns);
	const [items, setItems] = useState<IServiceListItem[]>([]);
	const [quickEditTarget, setQuickEditTarget] = useState<{
		saleOrderId: string;
		serviceId: string;
		agencyId?: number;
	} | null>(null);
	const [bulkEditOpen, setBulkEditOpen] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	const {
		selection,
		homogeneity,
		isSelected,
		toggleRow,
		togglePage,
		clearSelection,
		count: selectionCount,
	} = useServicesBulkSelection();

	const debouncedSearch = useDebounceState(searchValue, 300);

	const effectiveFilters = useMemo(
		() => applyServicesQuickDateFilters(filterApplied, quickDateState),
		[filterApplied, quickDateState],
	);
	const mergedFilters = useMemo(() => convertFiltersToRecord(effectiveFilters) ?? {}, [effectiveFilters]);
	const filtersKey = JSON.stringify(mergedFilters);
	const debouncedFiltersKey = useDebounceState(filtersKey, FILTER_FETCH_DEBOUNCE_MS);
	const displayItems = loading && lastDisplayedItemsRef.current.length > 0 ? lastDisplayedItemsRef.current : items;

	const visibleHeaderCells = useMemo((): IHeadCell[] => {
		if (!showOptionalColumns) return DEFAULT_VISIBLE_COLUMNS;
		return serviceListHeaderCells;
	}, [showOptionalColumns]);

	const sortableFields = useMemo(() => getSortableFields(visibleHeaderCells), [visibleHeaderCells]);

	const stickyClassByField = useMemo(() => {
		if (isSmallScreen || !showOptionalColumns) return new Map<string, string>();
		const map = new Map<string, string>();
		serviceListHeaderCells.forEach((cell, index) => {
			if (index < stickyWidthsServices.length) {
				map.set(cell.field, (classes as Record<string, string>)[`sticky${index + 1}`] || '');
			}
		});
		return map;
	}, [isSmallScreen, showOptionalColumns, classes]);

	const getStickyClass = (field: string) => stickyClassByField.get(field) ?? '';

	const isRefreshing = loading && !isInitialLoad;
	const showInitialSpinner = isInitialLoad && loading && lastDisplayedItemsRef.current.length === 0;
	const showEmptyState = !loading && !displayItems.length;
	const showRefetchPlaceholder = loading && !displayItems.length && !isInitialLoad;

	const applyParsedListState = useCallback((parsed: ReturnType<typeof parseServicesListState>) => {
		setSearchValue(parsed.search);
		setCurrentPage(parsed.page);
		setCurrentSize(parsed.size);
		setSortFields(parsed.sortFields);
		setFilterApplied(parsed.filterApplied);
		setQuickDateState(parsed.quickDateState);
		setShowOptionalColumns(parsed.showOptionalColumns);
	}, []);

	useEffect(() => {
		if (location.pathname !== routes.salesOrder.serviceList) {
			setIsListHydrated(false);
			return;
		}
		setIsListHydrated(true);
	}, [location.pathname]);

	useEffect(() => {
		const handlePopState = () => {
			if (window.location.pathname !== routes.salesOrder.serviceList) return;
			const parsed = parseServicesListState(normalizeServicesListSearch(window.location.search));
			applyParsedListState(parsed);
			lastPushedSearchRef.current = buildServicesListSearch({
				search: parsed.search,
				page: parsed.page,
				size: parsed.size,
				sortFields: parsed.sortFields,
				filterApplied: parsed.filterApplied,
				quickDateState: parsed.quickDateState,
			});
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [applyParsedListState]);

	useEffect(() => {
		if (!isListHydrated) return;

		const nextSearch = buildServicesListSearch({
			search: searchValue,
			page: currentPage,
			size: currentSize,
			sortFields,
			filterApplied,
			quickDateState,
		});

		if (areServicesListSearchesEqual(nextSearch, lastPushedSearchRef.current)) return;

		lastPushedSearchRef.current = nextSearch;
		const listSearch = toServicesListSearchParam(nextSearch);
		saveServicesListSearch(listSearch);
		window.history.replaceState(null, '', `${routes.salesOrder.serviceList}${listSearch}`);
	}, [searchValue, currentPage, currentSize, sortFields, filterApplied, quickDateState, isListHydrated]);

	useEffect(() => {
		if (!isListHydrated) return;

		const abortController = new AbortController();

		const loadData = async () => {
			try {
				setLoading(true);
				const { data: response } = await getServicesList(
					currentPage,
					currentSize,
					sortFields,
					mergedFilters,
					debouncedSearch || undefined,
					abortController.signal,
				);

				if (abortController.signal.aborted) return;

				const total = response.total ?? 0;
				setTotalRecords(total);
				setPageTotal(total === 0 ? 0 : Math.ceil(total / currentSize));
				const nextItems = response.elements ?? [];
				setItems(nextItems);
				lastDisplayedItemsRef.current = nextItems;
				setIsInitialLoad(false);
			} catch (error) {
				if (abortController.signal.aborted || axios.isCancel(error)) return;
				setAlert({
					show: true,
					severity: 'error',
					message: extractErrorMessage(error, 'Error al cargar el listado de servicios'),
				});
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false);
				}
			}
		};

		loadData();

		return () => {
			abortController.abort();
		};
	}, [currentPage, currentSize, sortFields, debouncedSearch, debouncedFiltersKey, isListHydrated, refreshKey]);

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
		setCurrentPage(1);
	};

	const handleFiltersChange = useCallback((filters: FilterModel[]) => {
		const { filters: nextFilters, clearedQuick } = stripQuickDateConflictsFromFilters(filters);
		setFilterApplied(nextFilters);
		if (Object.keys(clearedQuick).length > 0) {
			setQuickDateState((prev) => ({ ...prev, ...clearedQuick }));
		}
		setCurrentPage(1);
	}, []);

	const handleQuickDateChange = useCallback((next: ServicesQuickDateState) => {
		setQuickDateState(next);
		setFilterApplied((prev) =>
			prev.filter(
				(filter) =>
					!['created', 'createdFrom', 'createdTo', 'minStartDate', 'minStartDateFrom', 'minStartDateTo'].includes(
						filter.key,
					),
			),
		);
		setCurrentPage(1);
	}, []);

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const handleOpenService =
		(saleOrderId: string, serviceId: string, agencyId?: number) => (event: React.MouseEvent) => {
			openSaleOrderServiceUrl(history, saleOrderId, serviceId, event, agencyId);
		};

	const handleOpenQuickEdit =
		(saleOrderId: string, serviceId: string, agencyId?: number) => (event: React.MouseEvent) => {
			event.stopPropagation();
			setQuickEditTarget({ saleOrderId, serviceId, agencyId });
		};

	const handleToggleColumns = () => {
		setShowOptionalColumns((prev) => {
			const next = !prev;
			saveShowOptionalColumns(next);
			return next;
		});
	};

	const handleQuickEditSaved = () => {
		setRefreshKey((prev) => prev + 1);
	};

	const handleBulkCompleted = (results: BulkEditRowResult[]) => {
		setRefreshKey((prev) => prev + 1);
		if (results.every((item) => item.status === 'success')) {
			clearSelection();
		}
	};

	const pageSelectedCount = useMemo(
		() => displayItems.filter((row) => isSelected(row.id)).length,
		[displayItems, isSelected],
	);
	const allPageSelected = displayItems.length > 0 && pageSelectedCount === displayItems.length;
	const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

	const handleCloseAlert = () => setAlert(defaultAlertState);

	const getSortDirection = (field: string): 'asc' | 'desc' | false => {
		return sortFields.find((item) => item.field === field)?.order ?? false;
	};

	return (
		<>
			<Paper className={classes.filtersCard} elevation={0}>
				<Box className={classes.toolbarRow}>
					<TextField
						label="Buscar"
						placeholder="ID, referencia, SaleOrder, caso, notas..."
						variant="outlined"
						size="small"
						value={searchValue}
						onChange={handleSearchChange}
						sx={{ flex: 1, minWidth: 280 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="action" />
								</InputAdornment>
							),
						}}
					/>
					<Box className={classes.toolbarActions}>
						<Typography className={classes.resultsCount}>
							{totalRecords.toLocaleString('es-UY')} {totalRecords === 1 ? 'resultado' : 'resultados'}
						</Typography>
						<Tooltip title={showOptionalColumns ? 'Ocultar columnas adicionales' : 'Mostrar columnas adicionales'}>
							<Button
								size="small"
								variant={showOptionalColumns ? 'contained' : 'outlined'}
								startIcon={<ViewColumnIcon />}
								onClick={handleToggleColumns}
							>
								{showOptionalColumns ? 'Menos columnas' : 'Más columnas'}
							</Button>
						</Tooltip>
					</Box>
				</Box>
			</Paper>

			<ServicesQuickDateBar quickDateState={quickDateState} onQuickDateChange={handleQuickDateChange} />

			<ServicesFilters filters={filterApplied} onFiltersChange={handleFiltersChange} />

			<Paper className={`${classes.tablePaper} ${classes.tableWrapper}`} elevation={0}>
				{isRefreshing && (
					<Box className={classes.tableLoadingOverlay}>
						<CircularProgress size={18} />
						<Typography variant="caption" color="text.secondary">
							Actualizando...
						</Typography>
					</Box>
				)}
				<TableContainer className={`${classes.tableContainer} ${isRefreshing ? classes.tableRefreshing : ''}`}>
					<Table stickyHeader size="small" sx={{ minWidth: 650 }}>
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox" className={`${classes.tableHeader} ${classes.checkboxCell}`}>
									<Checkbox
										size="small"
										sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
										indeterminate={somePageSelected}
										checked={allPageSelected}
										onChange={(event) => togglePage(displayItems, event.target.checked)}
										disabled={!displayItems.length}
									/>
								</TableCell>
								{visibleHeaderCells.map((cell) => {
									const stickyClass = getStickyClass(cell.field);
									const sortDirection = getSortDirection(cell.field);

									return (
										<TableCell
											key={cell.field}
											align={cell.field === 'serviceType' ? 'left' : 'center'}
											sortDirection={sortDirection}
											className={`${classes.tableHeader} ${classes.cellNoWrap} ${stickyClass}`}
										>
											{cell.sortable ? (
												<TableSortLabel
													active={sortFields.some((item) => item.field === cell.field)}
													sx={{
														color: 'white !important',
														'& .MuiTableSortLabel-icon': { color: 'white !important' },
														'&:hover': { color: 'rgba(255,255,255,0.85) !important' },
													}}
													direction={sortDirection || 'asc'}
													onClick={createSortHandler(cell.field, sortFields, sortableFields, setSortFields)}
												>
													{cell.headerName}
												</TableSortLabel>
											) : (
												cell.headerName
											)}
										</TableCell>
									);
								})}
								<TableCell align="center" className={`${classes.tableHeader} ${classes.cellNoWrap}`}>
									Acciones
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{showInitialSpinner ? (
								<TableRow>
									<TableCell colSpan={visibleHeaderCells.length + 2} align="center" sx={{ py: 6 }}>
										<CircularProgress size={28} />
									</TableCell>
								</TableRow>
							) : showRefetchPlaceholder ? (
								<TableRow>
									<TableCell colSpan={visibleHeaderCells.length + 2} align="center" sx={{ py: 4 }}>
										<Box display="flex" alignItems="center" justifyContent="center" gap={1}>
											<CircularProgress size={22} />
											<Typography variant="body2" color="text.secondary">
												Actualizando resultados...
											</Typography>
										</Box>
									</TableCell>
								</TableRow>
							) : showEmptyState ? (
								<TableRow>
									<TableCell colSpan={visibleHeaderCells.length + 2}>
										<PageNoResult />
									</TableCell>
								</TableRow>
							) : (
								displayItems.map((row) => (
									<TableRow
										key={row.id}
										className={classes.tableRow}
										selected={isSelected(row.id)}
										onDoubleClick={handleOpenService(row.saleOrderId, row.id, row.agencyId)}
									>
										<TableCell padding="checkbox" className={classes.checkboxCell}>
											<Checkbox
												size="small"
												checked={isSelected(row.id)}
												onClick={(event) => event.stopPropagation()}
												onChange={() => toggleRow(row)}
											/>
										</TableCell>
										{visibleHeaderCells.map((cell) => {
											const stickyClass = getStickyClass(cell.field);
											const isIdCell = cell.field === 'id' || cell.field === 'saleOrderId';

											return (
												<TableCell
													key={cell.field}
													align={cell.field === 'serviceType' ? 'left' : 'center'}
													className={`${classes.cellNoWrap} ${stickyClass}`}
													onClick={isIdCell ? handleOpenService(row.saleOrderId, row.id, row.agencyId) : undefined}
												>
													{isIdCell
														? renderFullIdCell((row as Record<string, unknown>)[cell.field] as string)
														: serviceCellRenderers[cell.field]
														? serviceCellRenderers[cell.field](row)
														: transformServiceValue(cell.field, (row as Record<string, unknown>)[cell.field], row)}
												</TableCell>
											);
										})}
										<TableCell align="center" className={classes.cellNoWrap}>
											<Tooltip title="Edición rápida">
												<IconButton
													size="small"
													color="primary"
													onClick={handleOpenQuickEdit(row.saleOrderId, row.id, row.agencyId)}
												>
													<EditIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			{totalRecords > 0 && (
				<Box className={classes.paginationBar}>
					<Select value={currentSize} onChange={handleRowsPerPageChange} size="small">
						{pageSizeOptions.map((value) => (
							<MenuItem key={value} value={value}>
								{value} por página
							</MenuItem>
						))}
					</Select>
					<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
				</Box>
			)}

			<Snackbar open={alert.show} autoHideDuration={3000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity} onClose={handleCloseAlert}>
					{alert.message}
				</Alert>
			</Snackbar>

			<BulkSelectionBar
				count={selectionCount}
				saleOrderCount={homogeneity.saleOrderCount}
				agencyCount={homogeneity.agencyCount}
				isHomogeneous={homogeneity.isHomogeneous}
				serviceType={homogeneity.serviceType}
				onEdit={() => setBulkEditOpen(true)}
				onClear={clearSelection}
			/>

			{quickEditTarget && (
				<ServiceQuickEditDialog
					open
					saleOrderId={quickEditTarget.saleOrderId}
					serviceId={quickEditTarget.serviceId}
					agencyId={quickEditTarget.agencyId}
					onClose={() => setQuickEditTarget(null)}
					onSaved={handleQuickEditSaved}
				/>
			)}

			{bulkEditOpen && homogeneity.isHomogeneous && homogeneity.serviceType && (
				<ServicesBulkEditDialog
					open
					selection={selection}
					serviceType={homogeneity.serviceType}
					onClose={() => setBulkEditOpen(false)}
					onCompleted={handleBulkCompleted}
				/>
			)}
		</>
	);
};
